import { useCallback, useState } from "react";
import { RefreshCw } from "lucide-react";
import { clockApi, type Row } from "../../../api/clock";
import { PageHeader } from "../../../components/ComingSoon";
import { Button, StatusPill } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { DataTable, LoadState, useLoad } from "../shared";
import { EvidencePanel } from "./EvidencePanel";
import { LiveBoard } from "./LiveBoard";

type Kind = "requests" | "sessions" | "exceptions";
const STATUSES: Record<Kind, string[]> = {
  requests: ["PENDING_GPS", "CONFIRMED", "EXCEPTION"],
  sessions: ["CLOCKED_IN", "PENDING_CLOCK_OUT_GPS", "COMPLETED", "NEEDS_REVIEW"],
  exceptions: ["OPEN", "RESOLVED"],
};
const HEADERS: Record<Kind, string[]> = {
  requests: ["Text received", "Employee", "Type", "Status", "Result", "Codes / flags", ""],
  sessions: ["Clock-in text", "Employee", "Venue", "Status", "Clock-out text", "Payable in / out", "Flags"],
  exceptions: ["Text received", "Employee", "Type", "Code", "Status", "Employee note"],
};
const s = (v: unknown) => (v == null || v === "" ? "—" : String(v));
const list = (v: unknown) => (Array.isArray(v) && v.length ? v.join(", ") : "—");

function Cells({ kind, r, onEvidence }: { kind: Kind; r: Row; onEvidence: (id: string) => void }) {
  const td = "px-4 py-3 text-xs text-zinc-300";
  if (kind === "requests") {
    const res = r.result as { passed: boolean; distance_m: number | null } | null;
    return (<>
      <td className={td}>{fmtDate(r.sms_received_at as string)}</td><td className={td}>{s(r.employee_name)}</td>
      <td className={`${td} font-mono`}>{s(r.type)}</td><td className={td}><StatusPill status={s(r.status)} /></td>
      <td className={td}>{res ? `${res.passed ? "Pass" : "Fail"}${res.distance_m != null ? ` · ${res.distance_m} m` : ""}` : "—"}</td>
      <td className={`${td} font-mono`}>{list([...(r.exception_codes as string[]), ...((r.review_flags as string[]) ?? [])])}</td>
      <td className={td}><button type="button" onClick={() => onEvidence(r.id)} className="text-signal hover:text-white" data-testid={`timeclock-evidence-button-${r.id}`}>Evidence</button></td>
    </>);
  }
  if (kind === "sessions") return (<>
    <td className={td}>{fmtDate(r.in_sms_received_at as string)}</td><td className={td}>{s(r.employee_name)}</td><td className={td}>{s(r.venue_name)}</td>
    <td className={td}><StatusPill status={s(r.status)} /></td><td className={td}>{fmtDate(r.out_sms_received_at as string)}</td>
    <td className={td}>{fmtDate(r.payable_in_at as string)} / {fmtDate(r.payable_out_at as string)}</td>
    <td className={`${td} font-mono`}>{list(r.review_flags)}</td>
  </>);
  return (<>
    <td className={td}>{fmtDate(r.sms_received_at as string)}</td><td className={td}>{s(r.employee_name)}</td>
    <td className={`${td} font-mono`}>{s(r.type)}</td><td className={`${td} font-mono text-warn`}>{s(r.code)}</td>
    <td className={td}><StatusPill status={s(r.status)} /></td><td className={`${td} max-w-xs`}>{s(r.employee_note)}</td>
  </>);
}

export default function AdminTimeclock() {
  const [live, setLive] = useState(true);
  return (
    <section data-testid="admin-timeclock-page" className="animate-rise">
      <PageHeader title="Live Clock" />
      <div className="mb-6 flex gap-2">
        <button type="button" onClick={() => setLive(true)} data-testid="timeclock-tab-live" className={`rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${live ? "border-signal bg-signal/10 text-white" : "border-white/15 text-zinc-400"}`}>Live dashboard</button>
        <button type="button" onClick={() => setLive(false)} data-testid="timeclock-tab-activity" className={`rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${!live ? "border-signal bg-signal/10 text-white" : "border-white/15 text-zinc-400"}`}>Activity tables</button>
      </div>
      {live ? <LiveBoard /> : <ActivityTables />}
    </section>
  );
}

function ActivityTables() {
  const [kind, setKind] = useState<Kind>("requests");
  const [status, setStatus] = useState("");
  const [reload, setReload] = useState(0);
  const [evidence, setEvidence] = useState<string | null>(null);
  const loader = useCallback(() => clockApi.list(kind, status || undefined), [kind, status, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const sumLoader = useCallback(() => clockApi.summary(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<Row[]>(loader);
  const { data: sum } = useLoad(sumLoader);
  return (
    <section data-testid="timeclock-activity-tables">
      <PageHeader title="Requests, sessions & exceptions">
        <Button variant="secondary" onClick={() => setReload((r) => r + 1)} data-testid="timeclock-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </PageHeader>
      {sum && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
          {([["clock_enabled", "Clock"], ["pending_gps", "Pending GPS"], ["clocked_in", "Clocked in"], ["needs_review", "Needs review"], ["open_exceptions", "Open exceptions"]] as const).map(([k, l]) => (
            <div key={k} data-testid={`timeclock-summary-${k}`} className="rounded-sm border border-white/10 bg-surface p-4">
              <p className="label-caps text-zinc-500">{l}</p>
              <p className="mt-1 font-display text-3xl font-bold text-white">{typeof sum[k] === "boolean" ? (sum[k] ? "ON" : "OFF") : String(sum[k])}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(["requests", "sessions", "exceptions"] as Kind[]).map((k) => (
          <button key={k} type="button" onClick={() => { setKind(k); setStatus(""); }} data-testid={`timeclock-tab-${k}`}
            className={`rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${kind === k ? "border-signal bg-signal/10 text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            {k === "requests" ? "Punch requests" : k === "sessions" ? "Work sessions" : "Exceptions"}
          </button>
        ))}
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="timeclock-status-filter" className="ml-auto h-10 rounded-sm border border-white/15 bg-surface px-3 text-xs text-white">
          <option value="">All statuses</option>
          {STATUSES[kind].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <LoadState error={error} loading={!data && !error} testId="timeclock-load-state" />
      {data && (
        <DataTable testId={`timeclock-table-${kind}`} headers={HEADERS[kind]} empty={data.length === 0}>
          {data.map((r) => <tr key={r.id} data-testid={`timeclock-row-${r.id}`} className="hover:bg-white/[0.03]"><Cells kind={kind} r={r} onEvidence={setEvidence} /></tr>)}
        </DataTable>
      )}
      {evidence && <EvidencePanel requestId={evidence} onClose={() => setEvidence(null)} />}
    </section>
  );
}
